import { TableBody, useTheme } from "@mui/material"
import { PageDefaultSegmentWithHeader } from "@sis/react-library"
import { ScheduleMeetingsHeader } from "./ScheduleMeetingsHeader";
import { ScheduleMeetingsItem } from "./ScheduleMeetingsItem";
import dayjs from "dayjs";
import { RPMTable } from "../../../shared/table/RPMTable";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { scheduleState } from "../../../../redux/schedule/scheduleSlice";
import { rdsMeeting_Dto } from "../../../../interfaces/dtos/rdsMeeting";
import { ScheduleMeetingsPagination } from "./ScheduleMeetingsPagination";
import { scheduleVariables } from "../../../../variables";
import React, { useEffect } from "react";
import { loadRdsEventsMultiple } from "../../../../redux/rds/rdsActionsEvent";
import { segmentHeaderDefault } from "../../../../utils/commonUtils";

interface ScheduleMeetingsProps {
  meetings: rdsMeeting_Dto[];
  searchDate: Date;
  selectedMeetings: number[];
  onSelected: (meetingId: number) => void;
  onCollapsed: (open: boolean) => void;
}

export const ScheduleMeetings = ({
  meetings, searchDate, selectedMeetings, onSelected, onCollapsed
}: ScheduleMeetingsProps) => {

  const theme = useTheme();
  const state = useAppSelector(scheduleState);
  const dispatch = useAppDispatch();

  const [page, setPage] = React.useState(1);
  const [meetingIds, setMeetingIds] = React.useState<string>("");

  const paginate = () => {
    const pageCount = Math.ceil(meetings.length / scheduleVariables.meetingPageSize);
    return Array.from({ length: pageCount }, (_, index) =>
      meetings.slice(index * scheduleVariables.meetingPageSize, (index + 1) * scheduleVariables.meetingPageSize)
    );
  };

  const paginatedMeetings = paginate();
  const currentMeetings = paginatedMeetings[page - 1] ?? [];

  const loadEvents = () => {
    const meetingIds = currentMeetings.map(m => m.id);
    dispatch(loadRdsEventsMultiple(meetingIds));
  }

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    loadEvents();
    // we only want the conditions below
    // eslint-disable-next-line
  }, [page]);

  useEffect(() => {
    const currentMeetingIds = JSON.stringify(meetings.map(m => m.id).toSorted((a, b) => a - b));
    if (currentMeetingIds !== meetingIds) {
      // Meetings changed - set meeting ids
      // eslint-disable-next-line
      setMeetingIds(currentMeetingIds);
      setPage(1);
      loadEvents();
    }
    // we only want the conditions below
    // eslint-disable-next-line
  }, [meetings]);

  return (
    <PageDefaultSegmentWithHeader
      id='schedule-meetings'
      width='100%'
      headingText={`Meetings - ${dayjs(searchDate).format('YYYY-MM-DD')}`}
      headingBackground={segmentHeaderDefault(theme)}
      headingColor={theme.palette.common.white}
      headingHoverBackground={theme.palette.secondary.main}
      collapsable
      onCollapse={onCollapsed}
    >
      <RPMTable
        containerProps={{
          sx: {
            height: 'calc(50vh - 190px)',
            opacity: state.loadingSideMenu ? 0.2 : 1,
          }
        }}>
        <ScheduleMeetingsHeader />
        <TableBody>
          {currentMeetings.map((meeting) =>
            <ScheduleMeetingsItem
              key={meeting.id}
              meeting={meeting}
              selected={selectedMeetings.find((sm) => sm === meeting.id) !== undefined}
              onSelected={onSelected}
            />
          )}
          <ScheduleMeetingsPagination
            itemCount={meetings.length}
            page={page}
            onPageChange={handleChangePage}
          />
        </TableBody>
      </RPMTable>
    </PageDefaultSegmentWithHeader>
  )
}
